import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, Eye } from "lucide-react";
import { LeaveDetailsDialog, StatusBadge, TYPES, TYPE_LABEL, downloadBlob, fmtDays } from "@/components/leave/shared";

const STATUSES = ["Pending", "Approved", "Rejected", "Cancelled", "Cancellation Requested"];
const F0 = { q: "", department: "all", leave_type: "all", status: "Pending", date_from: "", date_to: "" };

const Pick = ({ value, onChange, all, items, testid, w = "w-40" }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={`rounded-none h-9 text-xs ${w}`} data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{all}</SelectItem>
      {items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const HrApplications = () => {
  const [f, setF] = useState(F0);
  const [rows, setRows] = useState([]);
  const [depts, setDepts] = useState([]);
  const [view, setView] = useState(null);
  const [remarks, setRemarks] = useState("");

  const load = () => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v && v !== "all"));
    api.get("/leave/applications", { params }).then((r) => setRows(r.data));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [f]); // eslint-disable-line
  useEffect(() => { api.get("/leave/departments").then((r) => setDepts(r.data)); }, []);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const act = async (action) => {
    try {
      const r = await api.post(`/leave/applications/${view.id}/hr-action`, { action, remarks });
      toast.success("Updated");
      setView(r.data); setRemarks("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  return (
    <div data-testid="hr-applications">
      <div className="flex flex-wrap gap-2 mb-3 items-center">
        <Input value={f.q} onChange={set("q")} placeholder="Employee name / ID" className="rounded-none h-9 text-xs w-52" data-testid="hr-filter-q" />
        <Pick value={f.department} onChange={set("department")} all="All departments" items={depts.map((d) => [d, d])} testid="hr-filter-dept" />
        <Pick value={f.leave_type} onChange={set("leave_type")} all="All leave types" items={TYPES.map((t) => [t, TYPE_LABEL[t]])} testid="hr-filter-type" />
        <Pick value={f.status} onChange={set("status")} all="All statuses" items={STATUSES.map((s) => [s, s])} testid="hr-filter-status" w="w-48" />
        <Input type="date" value={f.date_from} onChange={set("date_from")} className="rounded-none h-9 text-xs w-36" data-testid="hr-filter-from" />
        <Input type="date" value={f.date_to} onChange={set("date_to")} className="rounded-none h-9 text-xs w-36" data-testid="hr-filter-to" />
        <Button variant="outline" className="rounded-none h-9 text-xs uppercase tracking-widest font-bold ml-auto" data-testid="hr-export"
          onClick={() => downloadBlob(`/leave/applications/export?${new URLSearchParams(Object.fromEntries(Object.entries(f).filter(([, v]) => v && v !== "all")))}`, "leave-applications.xlsx").catch(() => toast.error("Export failed"))}><Download className="w-4 h-4 mr-1" /> Excel</Button>
      </div>
      <div className="border border-[#E5E7EB] overflow-x-auto bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAFA] border-b border-[#E5E7EB]">
            <tr>{["Employee", "Emp ID", "Department", "Designation", "Type", "From", "To", "Days", "Applied", "Available", "Reason", "Status", ""].map((h) => <th key={h} className="text-left px-3 py-2 label-eyebrow whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={13} className="px-3 py-8 text-center text-[#9CA3AF]">No applications match.</td></tr>}
            {rows.map((a) => (
              <tr key={a.id} className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA]" data-testid={`hr-leave-row-${a.id}`}>
                <td className="px-3 py-2 font-bold whitespace-nowrap">{a.employee_name}</td>
                <td className="px-3 py-2 text-xs">{a.employee_code || "—"}</td>
                <td className="px-3 py-2 text-xs">{a.department || "—"}</td>
                <td className="px-3 py-2 text-xs">{a.designation || "—"}</td>
                <td className="px-3 py-2 font-bold">{a.leave_type}{a.duration === "half" ? " ½" : ""}</td>
                <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtDate(a.from_date)}</td>
                <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtDate(a.to_date)}</td>
                <td className="px-3 py-2">{fmtDays(a.days)}</td>
                <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtDate(a.applied_at)}</td>
                <td className="px-3 py-2 font-bold">{fmtDays(a.available_balance)}</td>
                <td className="px-3 py-2 text-xs max-w-[200px] truncate">{a.reason}</td>
                <td className="px-3 py-2"><StatusBadge app={a} /></td>
                <td className="px-3 py-2"><button onClick={() => setView(a)} className="text-[#002FA7]" data-testid={`hr-leave-view-${a.id}`}><Eye className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <LeaveDetailsDialog app={view} onClose={() => { setView(null); setRemarks(""); }}>
        {view && <HrActions app={view} remarks={remarks} setRemarks={setRemarks} act={act} />}
      </LeaveDetailsDialog>
    </div>
  );
};

const HrActions = ({ app, remarks, setRemarks, act }) => {
  const btn = "rounded-none uppercase tracking-widest text-xs font-bold h-9";
  const pending = app.status === "Pending";
  const approved = app.status === "Approved";
  if (!pending && !approved) return null;
  return (
    <div className="mt-4 space-y-2 border-t border-[#E5E7EB] pt-4">
      {pending && app.stage === "Manager" && <div className="text-xs text-[#B45309]" data-testid="hr-awaiting-manager">Awaiting reporting manager approval — HR can reject or ask for clarification meanwhile.</div>}
      <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} className="rounded-none" placeholder="HR remarks (required for reject, clarification and cancellation)" data-testid="hr-remarks" />
      <div className="flex flex-wrap gap-2">
        {pending && <Button disabled={app.stage === "Manager"} className={`${btn} bg-[#10B981] hover:bg-[#059669]`} onClick={() => act("approve")} data-testid="hr-approve">Approve</Button>}
        {pending && <Button variant="outline" className={`${btn} text-[#B91C1C] border-[#B91C1C]`} onClick={() => act("reject")} data-testid="hr-reject">Reject</Button>}
        {pending && <Button variant="outline" className={btn} onClick={() => act("clarify")} data-testid="hr-clarify">Request Clarification</Button>}
        {approved && app.cancellation_requested && <Button className={`${btn} bg-[#B91C1C] hover:bg-[#991B1B]`} onClick={() => act("approve_cancel")} data-testid="hr-approve-cancel">Approve Cancellation</Button>}
        {approved && app.cancellation_requested && <Button variant="outline" className={btn} onClick={() => act("decline_cancel")} data-testid="hr-decline-cancel">Decline Cancellation</Button>}
        {approved && !app.cancellation_requested && <Button variant="outline" className={`${btn} text-[#B91C1C] border-[#B91C1C]`} onClick={() => act("cancel")} data-testid="hr-cancel">Cancel Approved Leave</Button>}
      </div>
    </div>
  );
};
